import React, { useState, useEffect, useMemo, useRef } from "react";
import { X, Search, Plus, Printer } from "lucide-react";
import { toast } from "sonner";
import { EntidadModal } from "../pages/CatastroPage";

const BTN_PRI = "flex items-center gap-2 px-4 py-2 text-sm bg-arandu-blue hover:bg-blue-600 text-white rounded-lg transition-all font-body disabled:opacity-50";
const BTN_SEC = "flex items-center gap-2 px-3 py-2 text-sm bg-white/5 hover:bg-white/10 text-slate-300 rounded-lg transition-all border border-white/10";
const INPUT = "w-full bg-arandu-dark border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-arandu-blue/60";
const TEXTAREA = INPUT + " resize-none";
const LABEL = "block text-xs text-slate-400 mb-1";

// Mismo patrón que ClienteSearchSelect de PresupuestoFormModal.jsx (buscador con
// creación inline en el Catastro), copiado acá para no acoplar los dos módulos.
const ClienteSearchSelect = ({ empresas, value, onSelect, onCreated, token, logo }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [clienteModalNombre, setClienteModalNombre] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return (empresas || []).slice(0, 12);
    const q = query.toLowerCase();
    return (empresas || []).filter(e =>
      (e.nombre || "").toLowerCase().includes(q) ||
      (e.razon_social || "").toLowerCase().includes(q) ||
      (e.ruc || "").toLowerCase().includes(q)
    ).slice(0, 12);
  }, [empresas, query]);

  const exactMatch = (empresas || []).some(e => (e.nombre || e.razon_social || "").toLowerCase() === query.trim().toLowerCase());

  const handlePick = (emp) => {
    onSelect(emp);
    setQuery(""); setOpen(false); setClienteModalNombre("");
  };

  if (value?.id) {
    return (
      <div className="flex items-center gap-2 bg-arandu-dark border border-arandu-blue/30 rounded-md px-3 py-2.5 min-h-[46px]">
        <div className="flex-1 min-w-0">
          <span className="text-white text-sm font-medium truncate block">{value.nombre || value.razon_social}</span>
          {value.ruc && <span className="text-slate-500 text-xs">RUC: {value.ruc}</span>}
        </div>
        <button type="button" onClick={() => onSelect(null)} className="text-slate-500 hover:text-white shrink-0 transition-colors">
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <div ref={ref} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500 pointer-events-none" />
        <input
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Buscar cliente por nombre o RUC..."
          className="w-full bg-arandu-dark border border-white/10 text-white rounded-md pl-9 pr-3 py-3 text-sm focus:outline-none focus:border-arandu-blue placeholder:text-slate-600"
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-arandu-dark border border-white/10 rounded-lg shadow-xl overflow-hidden">
          {filtered.length > 0 && (
            <ul className="max-h-48 overflow-y-auto divide-y divide-white/5">
              {filtered.map(e => (
                <li key={e.id} onMouseDown={() => handlePick(e)}
                  className="px-3 py-2.5 hover:bg-white/5 cursor-pointer flex items-center justify-between">
                  <span className="text-white text-sm">{e.nombre || e.razon_social}</span>
                  {e.ruc && <span className="text-slate-500 text-xs ml-2 shrink-0">RUC: {e.ruc}</span>}
                </li>
              ))}
            </ul>
          )}
          {filtered.length === 0 && query.trim() && (
            <div className="px-3 py-2 text-slate-500 text-xs">Sin resultados para "{query.trim()}"</div>
          )}
          {query.trim() && !exactMatch && (
            <div className={filtered.length > 0 ? "border-t border-white/5" : ""}>
              <button type="button" onMouseDown={() => setClienteModalNombre(query.trim())}
                className="w-full px-3 py-2.5 text-left text-sm text-emerald-400 hover:bg-emerald-500/10 flex items-center gap-2 transition-colors">
                <Plus className="w-3.5 h-3.5 shrink-0" />
                Crear "<strong>{query.trim()}</strong>" como nueva entidad
              </button>
            </div>
          )}
        </div>
      )}
      {clienteModalNombre !== "" && (
        <EntidadModal
          entidad={{
            _presetRol: "es_cliente",
            _initialNombre: clienteModalNombre,
            _initialRazonSocial: clienteModalNombre,
          }}
          logo={logo}
          token={token}
          allowedRoles={{ es_cliente: true, es_proveedor: false, es_empleado: false }}
          onClose={() => setClienteModalNombre("")}
          onSaved={nuevo => {
            if (nuevo?.id) {
              onCreated?.(nuevo);
              handlePick(nuevo);
            } else {
              setClienteModalNombre("");
            }
          }}
        />
      )}
    </div>
  );
};

// ── Patrón de desbloqueo (grilla 3x3 como en Android): se dibuja con mouse o dedo ──
const PAT_PTS = [1, 2, 3, 4, 5, 6, 7, 8, 9].map(n => ({ n, x: 40 + ((n - 1) % 3) * 60, y: 40 + Math.floor((n - 1) / 3) * 60 }));

const PatronPad = ({ value, onChange, disabled }) => {
  const svgRef = useRef(null);
  const [dibujando, setDibujando] = useState(false);
  const [cursor, setCursor] = useState(null);
  const seq = value || [];

  const aCoord = (e) => {
    const r = svgRef.current.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * 200, y: ((e.clientY - r.top) / r.height) * 200 };
  };
  const tocar = (pt, actual) => {
    const hit = PAT_PTS.find(p => Math.hypot(p.x - pt.x, p.y - pt.y) < 24);
    if (hit && !actual.includes(hit.n)) { const nuevo = [...actual, hit.n]; onChange(nuevo); return nuevo; }
    return actual;
  };
  const down = (e) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const pt = aCoord(e);
    setDibujando(true); setCursor(pt);
    const hit = PAT_PTS.find(p => Math.hypot(p.x - pt.x, p.y - pt.y) < 24);
    onChange(hit ? [hit.n] : []);     // empezar de nuevo
  };
  const move = (e) => {
    if (!dibujando) return;
    const pt = aCoord(e); setCursor(pt);
    tocar(pt, seq);
  };
  const up = () => { setDibujando(false); setCursor(null); };
  const puntos = seq.map(n => PAT_PTS[n - 1]);

  return (
    <div className="flex items-center gap-3">
      <svg ref={svgRef} viewBox="0 0 200 200" width="150" height="150"
        style={{ touchAction: "none", cursor: disabled ? "default" : "crosshair" }}
        className="bg-arandu-dark border border-white/10 rounded-lg select-none"
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
        {puntos.length > 1 && <polyline points={puntos.map(p => `${p.x},${p.y}`).join(" ")} fill="none" stroke="#3b82f6" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />}
        {dibujando && puntos.length > 0 && cursor && (
          <line x1={puntos[puntos.length - 1].x} y1={puntos[puntos.length - 1].y} x2={cursor.x} y2={cursor.y} stroke="#3b82f6" strokeWidth="3" strokeOpacity="0.5" />
        )}
        {PAT_PTS.map(p => {
          const idx = seq.indexOf(p.n);
          return (
            <g key={p.n}>
              <circle cx={p.x} cy={p.y} r="11" fill={idx >= 0 ? "#3b82f6" : "#1e293b"} stroke={idx >= 0 ? "#93c5fd" : "#475569"} strokeWidth="2" />
              {idx >= 0 && <text x={p.x} y={p.y + 4} textAnchor="middle" fontSize="11" fontWeight="bold" fill="#fff">{idx + 1}</text>}
            </g>
          );
        })}
      </svg>
      <div className="text-xs text-slate-400 space-y-2">
        <p>{seq.length ? `Patrón: ${seq.join(" → ")}` : "Dibujá el patrón del equipo (arrastrá sobre los puntos)."}</p>
        {!disabled && seq.length > 0 && (
          <button type="button" onClick={() => onChange([])} className="px-2 py-1 rounded bg-white/5 hover:bg-white/10 border border-white/10 text-slate-300">Borrar patrón</button>
        )}
      </div>
    </div>
  );
};

// SVG del patrón para la boleta impresa (puntos numerados y recorrido con flecha de inicio)
const patronSvgImpresion = (seq) => {
  if (!seq || !seq.length) return "";
  const pts = seq.map(n => PAT_PTS[n - 1]);
  const line = pts.length > 1 ? `<polyline points="${pts.map(p => `${p.x},${p.y}`).join(" ")}" fill="none" stroke="#111" stroke-width="4" stroke-linecap="round" stroke-linejoin="round"/>` : "";
  const dots = PAT_PTS.map(p => {
    const idx = seq.indexOf(p.n);
    return `<circle cx="${p.x}" cy="${p.y}" r="10" fill="${idx >= 0 ? "#111" : "#fff"}" stroke="#111" stroke-width="2"/>` +
      (idx >= 0 ? `<text x="${p.x}" y="${p.y + 4}" text-anchor="middle" font-size="11" font-weight="bold" fill="#fff">${idx + 1}</text>` : "");
  }).join("");
  return `<svg viewBox="0 0 200 200" width="110" height="110" style="border:1px solid #999;border-radius:6px">${line}${dots}</svg>`;
};

const TEXTO_LEGAL_DEFAULT = "Para cualquier reclamo es imprescindible la presentación de esta boleta.\nPasados los 30 días de la fecha de ingreso y no retirado el aparato pasará a ser propiedad de la casa, en concepto de pago nuestro y mano de obra, pudiendo esta dar cualquier destino, perdiendo el cliente cualquier derecho de reclamo.";
const escHtml = (t) => String(t ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

function imprimirComprobante(recepcion, empresaEmisora, textoLegal) {
  const w = window.open("", "_blank");
  if (!w) { toast.error("El navegador bloqueó la ventana de impresión"); return; }
  const emisorNombre = empresaEmisora?.razon_social || empresaEmisora?.nombre || "Arandu&JAR Informática";
  const emisorRuc = empresaEmisora?.ruc || "";
  const fecha = recepcion.fecha || new Date().toISOString().slice(0, 10);
  const html = `
    <html><head><title>Recepción ${recepcion.numero}</title>
    <style>
      body { font-family: Arial, sans-serif; padding: 24px; color: #111; }
      h1 { font-size: 18px; margin: 0 0 4px; }
      .sub { color: #555; font-size: 12px; margin-bottom: 16px; }
      .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 16px; }
      .box { border: 1px solid #ccc; border-radius: 6px; padding: 10px 12px; }
      .box b { display: block; font-size: 10px; text-transform: uppercase; color: #666; margin-bottom: 4px; }
      .full { grid-column: 1 / -1; }
      .legal { border: 1px solid #999; border-radius: 6px; padding: 12px 16px; margin: 4px 0 14px; font-size: 13px; line-height: 1.5; text-align: center; }
      .firma { margin-top: 60px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px; text-align: center; font-size: 12px; }
      .firma div { border-top: 1px solid #333; padding-top: 6px; }
    </style></head>
    <body>
      <h1>${emisorNombre}</h1>
      <div class="sub">${emisorRuc ? "RUC: " + emisorRuc : ""}</div>
      <h2 style="font-size:15px;margin:0 0 12px;">RECEPCIÓN DE EQUIPO N° ${recepcion.numero}</h2>
      <div class="grid">
        <div class="box"><b>Cliente</b>${recepcion.cliente_nombre || ""}${recepcion.cliente_ruc ? " — RUC: " + recepcion.cliente_ruc : ""}</div>
        <div class="box"><b>Fecha de ingreso</b>${fecha}</div>
        <div class="box"><b>Marca / Modelo</b>${recepcion.marca_modelo || ""}</div>
        <div class="box"><b>N° de serie</b>${recepcion.nro_serie || "-"}</div>
        <div class="box"><b>Tipo de aparato</b>${recepcion.tipo_aparato || "-"}</div>
        <div class="box"><b>Estado</b>${recepcion.estado || "recibido"}</div>
        <div class="box full"><b>Falla reclamada</b>${recepcion.falla_reclamada || ""}</div>
        <div class="box full"><b>Estado estético (golpes, rayas, etc.)</b>${recepcion.estado_estetico || "-"}</div>
        <div class="box full"><b>Accesorios que deja el cliente</b>${recepcion.accesorios || "-"}</div>
        ${recepcion.observaciones ? `<div class="box full"><b>Observaciones</b>${escHtml(recepcion.observaciones)}</div>` : ""}
        ${(recepcion.contrasena_equipo || (recepcion.patron_desbloqueo || []).length) ? `<div class="box full" style="display:flex;gap:24px;align-items:center;">
          <div style="flex:1"><b>Contraseña / PIN del equipo</b><span style="font-size:15px;font-family:monospace;">${escHtml(recepcion.contrasena_equipo || "-")}</span></div>
          ${(recepcion.patron_desbloqueo || []).length ? `<div style="text-align:center"><b>Patrón de ingreso</b>${patronSvgImpresion(recepcion.patron_desbloqueo)}<div style="font-size:10px;color:#555;">Secuencia: ${recepcion.patron_desbloqueo.join(" → ")}</div></div>` : ""}
        </div>` : ""}
      </div>
      ${(textoLegal ?? TEXTO_LEGAL_DEFAULT).trim() ? `<div class="legal">${escHtml(textoLegal ?? TEXTO_LEGAL_DEFAULT).replace(/\n/g, "<br/>")}</div>` : ""}
      <p style="font-size:11px;color:#555;">El presupuesto de reparación (N° ${recepcion.presupuesto_numero || "-"}) se emitirá una vez realizado el diagnóstico técnico del aparato.</p>
      <div class="firma">
        <div>Firma del cliente</div>
        <div>Recibido por (taller)</div>
      </div>
      <script>window.onload = () => setTimeout(() => window.print(), 200);</script>
    </body></html>`;
  w.document.write(html);
  w.document.close();
}

const RecepcionEquipoFormModal = ({
  recepcion, // si viene, es edición (diagnóstico/estado); si no, es alta nueva
  onClose,
  onSaved,
  token,
  API,
  empresas,
  activeEmpresaPropia,
  canEditar = false,
}) => {
  const isEdit = !!recepcion?.id;
  const [textoLegal, setTextoLegal] = useState(null);
  useEffect(() => {
    const lt = activeEmpresaPropia?.slug;
    if (!lt || !token) return;
    fetch(`${API}/admin/presupuestos/recepcion-config/texto?logo_tipo=${encodeURIComponent(lt)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null)).then(d => { if (d) setTextoLegal(d.texto_legal); }).catch(() => {});
  }, [activeEmpresaPropia?.slug, token, API]);
  const [clienteSel, setClienteSel] = useState(
    isEdit
      ? { id: recepcion.empresa_id, nombre: recepcion.cliente_nombre, ruc: recepcion.cliente_ruc }
      : null
  );
  const [empresasLocal, setEmpresasLocal] = useState(empresas || []);
  useEffect(() => { setEmpresasLocal(empresas || []); }, [empresas]);

  const [form, setForm] = useState({
    marca_modelo: recepcion?.marca_modelo || "",
    tipo_aparato: recepcion?.tipo_aparato || "",
    nro_serie: recepcion?.nro_serie || "",
    estado_estetico: recepcion?.estado_estetico || "",
    accesorios: recepcion?.accesorios || "",
    falla_reclamada: recepcion?.falla_reclamada || "",
    observaciones: recepcion?.observaciones || "",
    contrasena_equipo: recepcion?.contrasena_equipo || "",
    patron_desbloqueo: recepcion?.patron_desbloqueo || [],
    diagnostico: recepcion?.diagnostico || "",
    estado: recepcion?.estado || "recibido",
  });
  const [saving, setSaving] = useState(false);

  const ESTADOS = ["recibido", "en_diagnostico", "presupuestado", "en_reparacion", "reparado", "entregado", "cancelado"];
  const ESTADO_LABEL = {
    recibido: "Recibido", en_diagnostico: "En diagnóstico", presupuestado: "Presupuestado",
    en_reparacion: "En reparación", reparado: "Reparado", entregado: "Entregado", cancelado: "Cancelado",
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleGuardar = async () => {
    if (!isEdit && !clienteSel?.id) { toast.error("Seleccioná un cliente"); return; }
    if (!isEdit && !form.marca_modelo.trim()) { toast.error("Ingresá la marca/modelo del aparato"); return; }
    if (!isEdit && !form.falla_reclamada.trim()) { toast.error("Ingresá la falla reclamada por el cliente"); return; }
    setSaving(true);
    try {
      let res, data;
      if (isEdit) {
        res = await fetch(`${API}/admin/presupuestos/recepcion/${recepcion.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            marca_modelo: form.marca_modelo, tipo_aparato: form.tipo_aparato, nro_serie: form.nro_serie,
            estado_estetico: form.estado_estetico, accesorios: form.accesorios,
            falla_reclamada: form.falla_reclamada, observaciones: form.observaciones,
            contrasena_equipo: form.contrasena_equipo, patron_desbloqueo: form.patron_desbloqueo,
            diagnostico: form.diagnostico, estado: form.estado,
          }),
        });
      } else {
        res = await fetch(`${API}/admin/presupuestos/recepcion`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            empresa_id: clienteSel.id,
            logo_tipo: activeEmpresaPropia?.slug || "arandujar",
            marca_modelo: form.marca_modelo, tipo_aparato: form.tipo_aparato, nro_serie: form.nro_serie,
            estado_estetico: form.estado_estetico, accesorios: form.accesorios,
            falla_reclamada: form.falla_reclamada, observaciones: form.observaciones,
            contrasena_equipo: form.contrasena_equipo, patron_desbloqueo: form.patron_desbloqueo,
          }),
        });
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Error al guardar la recepción");
        return;
      }
      data = await res.json();
      toast.success(isEdit ? "Recepción actualizada" : `Recepción ${data.numero} registrada — Presupuesto ${data.presupuesto_numero} generado en borrador`);
      onSaved?.(data);
      if (!isEdit) imprimirComprobante(data, activeEmpresaPropia, textoLegal);
    } catch (e) {
      toast.error("Error de conexión al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0f1420] border border-white/10 rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">
            {isEdit ? `Recepción ${recepcion.numero} — ${recepcion.marca_modelo}` : "Nueva Recepción de Equipo"}
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        {!isEdit && (
          <div className="mb-4">
            <label className={LABEL}>Cliente</label>
            <ClienteSearchSelect
              empresas={empresasLocal}
              value={clienteSel}
              onSelect={setClienteSel}
              onCreated={(nuevo) => setEmpresasLocal(prev => prev.some(e => String(e.id) === String(nuevo.id)) ? prev : [...prev, nuevo])}
              token={token}
              logo={activeEmpresaPropia?.slug || "arandujar"}
            />
          </div>
        )}

        {isEdit && (
          <div className="mb-4 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5">
            <span className="text-white text-sm font-medium">{recepcion.cliente_nombre}</span>
            {recepcion.cliente_ruc && <span className="text-slate-500 text-xs ml-2">RUC: {recepcion.cliente_ruc}</span>}
            <div className="text-slate-500 text-xs mt-1">Presupuesto vinculado: N° {recepcion.presupuesto_numero || "-"}</div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={LABEL}>Marca / Modelo *</label>
            <input className={INPUT} value={form.marca_modelo} onChange={e => set("marca_modelo", e.target.value)}
              placeholder="Ej: Samsung Galaxy A52" disabled={isEdit} />
          </div>
          <div>
            <label className={LABEL}>N° de serie / IMEI</label>
            <input className={INPUT} value={form.nro_serie} onChange={e => set("nro_serie", e.target.value)} disabled={isEdit} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={LABEL}>Tipo de aparato</label>
            <input className={INPUT} value={form.tipo_aparato} onChange={e => set("tipo_aparato", e.target.value)}
              placeholder="Ej: Celular, Notebook, Impresora..." disabled={isEdit} />
          </div>
          {isEdit && (
            <div>
              <label className={LABEL}>Estado</label>
              <select className={INPUT} value={form.estado} onChange={e => set("estado", e.target.value)} disabled={!canEditar}>
                {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABEL[e]}</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="mb-3">
          <label className={LABEL}>Falla reclamada por el cliente *</label>
          <textarea className={TEXTAREA} rows={2} value={form.falla_reclamada}
            onChange={e => set("falla_reclamada", e.target.value)} disabled={isEdit && !canEditar} />
        </div>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className={LABEL}>Estado estético (golpes, rayas, etc.)</label>
            <textarea className={TEXTAREA} rows={2} value={form.estado_estetico}
              onChange={e => set("estado_estetico", e.target.value)} disabled={isEdit && !canEditar} />
          </div>
          <div>
            <label className={LABEL}>Accesorios que deja el cliente</label>
            <textarea className={TEXTAREA} rows={2} value={form.accesorios}
              onChange={e => set("accesorios", e.target.value)} placeholder="Cargador, funda, caja..." disabled={isEdit && !canEditar} />
          </div>
        </div>

        {isEdit && (
          <div className="mb-3">
            <label className={LABEL}>Diagnóstico técnico</label>
            <textarea className={TEXTAREA} rows={3} value={form.diagnostico}
              onChange={e => set("diagnostico", e.target.value)} disabled={!canEditar}
              placeholder="Lo carga el técnico luego de revisar el aparato. Los costos/repuestos se cargan en el Presupuesto vinculado." />
          </div>
        )}

        <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3 border border-white/10 rounded-lg p-3">
          <div>
            <label className={LABEL}>Contraseña / PIN del equipo</label>
            <input className={INPUT} value={form.contrasena_equipo} onChange={e => set("contrasena_equipo", e.target.value)}
              placeholder="Clave que informa el cliente" disabled={isEdit && !canEditar} autoComplete="off" />
            <p className="text-[11px] text-slate-500 mt-1">Se imprime en la boleta de recepción.</p>
          </div>
          <div>
            <label className={LABEL}>Patrón de ingreso (dibujar)</label>
            <PatronPad value={form.patron_desbloqueo} onChange={v => set("patron_desbloqueo", v)} disabled={isEdit && !canEditar} />
          </div>
        </div>

        <div className="mb-4">
          <label className={LABEL}>Observaciones</label>
          <textarea className={TEXTAREA} rows={2} value={form.observaciones}
            onChange={e => set("observaciones", e.target.value)} disabled={isEdit && !canEditar} />
        </div>

        <div className="flex justify-between items-center pt-3 border-t border-white/10">
          {isEdit ? (
            <button onClick={() => imprimirComprobante(recepcion, activeEmpresaPropia, textoLegal)} className={BTN_SEC}>
              <Printer className="w-4 h-4" /> Reimprimir comprobante
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className={BTN_SEC}>Cancelar</button>
            {(!isEdit || canEditar) && (
              <button onClick={handleGuardar} disabled={saving} className={BTN_PRI}>
                {saving ? "Guardando..." : "Guardar"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecepcionEquipoFormModal;
